import { FC, ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@new/lib';

interface RequirementGroupCardProps {
  id?: string;
  title: string;
  subtitle?: string;
  satisfiedCredits: number;
  requiredCredits: number;
  defaultOpen?: boolean;
  showProgress?: boolean;
  children: ReactNode;
}

export const RequirementGroupCard: FC<RequirementGroupCardProps> = ({
  id,
  title,
  subtitle,
  satisfiedCredits,
  requiredCredits,
  defaultOpen = true,
  showProgress = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const pct =
    requiredCredits > 0 ? Math.min(100, Math.round((satisfiedCredits / requiredCredits) * 100)) : 0;

  return (
    <div
      id={id}
      className="rounded-xl border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000)"
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-start justify-between gap-4 p-4 text-left"
      >
        <div className="flex flex-col gap-1">
          <span className="[font-size:var(--fs-p3)]! font-bold text-(--neutral-black-NB900)!">
            {title}
          </span>
          {subtitle ? (
            <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
              {subtitle}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end gap-1">
            <span className="[font-size:var(--fs-l5)]! font-semibold text-right text-(--neutral-black-NB700)!">
              {satisfiedCredits} of {requiredCredits} credits
            </span>
            {showProgress ? (
              <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-(--neutral-black-NB200) sm:w-24">
                <span
                  className="block h-full rounded-full bg-(--success-S600)"
                  style={{ width: `${pct}%` }}
                />
              </span>
            ) : null}
          </div>
          <ChevronDown
            className={cn(
              'size-5 shrink-0 text-(--neutral-black-NB600) transition-transform',
              open ? 'rotate-180' : ''
            )}
          />
        </div>
      </button>

      {open ? (
        <div className="flex flex-col gap-2 border-t border-(--neutral-black-NB200) p-4">
          {children}
        </div>
      ) : null}
    </div>
  );
};
